<template>
    <!-- If no company yet, show a fallback message -->
    <v-alert v-if="!company" type="warning">
        <p>{{ $t("company.noAssociatedCompany") }}</p>
    </v-alert>

    <!-- Once we have a company, display multiple BulletCards -->
    <div v-else class="company-details-view">
    <v-row>
        <!-- Left column: Company, Contact, Fleet, Ratings -->
        <v-col cols="12" md="6">
            <BulletCard
                class="mt-3"
                :cardTitle="$t('company.cardFirmaTitle')"
                titleFontSize="20px"
                :bullets="firmaBullets"
            />
            <BulletCard
                class="mt-3"
                :cardTitle="$t('company.cardContactTitle')"
                titleFontSize="20px"
                :bullets="contactBullets"
            />
            <FleetEditor
                class="mt-3"
                :fleet="company.fleet || []"
                :company-id="company.id"
                :can-edit="canManageDocuments"
                @saved="fetchCompany(company.id)"
            />
            <v-card class="mt-3">
                <v-tooltip location="bottom" :open-delay="500">
                    <template #activator="{ props: tooltipProps }">
                        <v-toolbar
                            v-bind="tooltipProps"
                            color="primary"
                            density="compact"
                            class="cursor-pointer"
                            @click="ratingsExpanded = !ratingsExpanded"
                        >
                            <v-toolbar-title class="text-white">
                                {{ $t('company.cardRatingsTitle') }}
                            </v-toolbar-title>
                            <v-spacer />
                            <span
                                v-if="ratings.ratings?.length && ratings.average != null"
                                class="text-yellow font-weight-bold mr-2"
                            >
                                {{ ratings.average.toFixed(1) }} / 5
                            </span>
                        </v-toolbar>
                    </template>
                    {{ $t('company.ratingsToolbarHint') }}
                </v-tooltip>
                <v-expand-transition>
                    <div v-show="ratingsExpanded">
                        <v-card-text
                            class="py-0 overflow-y-auto"
                            style="max-height: 150px"
                        >
                            <v-list v-if="ratingsLoading || !ratings.ratings?.length" density="compact">
                                <v-list-item density="compact">
                                    <v-list-item-title>
                                        {{ ratingsLoading ? $t("company.loading") : $t("company.noRatings") }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                            <v-list v-else density="compact">
                                <v-list-item
                                    v-for="r in ratings.ratings"
                                    :key="r.id"
                                    density="compact"
                                    class="rating-row"
                                >
                                    <template #default>
                                        <v-tooltip location="top">
                                            <template #activator="{ props }">
                                                <div v-bind="props" class="d-flex align-center flex-grow-1 rating-row-content">
                                                <span class="font-weight-medium">{{ r.rater_company_name || '—' }}</span>
                                                <span class="ml-2">{{ formatRatingDate(r.created_at) }}</span>
                                                <span class="d-inline-flex align-center ml-2">
                                                    <v-rating
                                                        :model-value="ratingAverage(r)"
                                                        :length="5"
                                                        size="small"
                                                        color="amber"
                                                        density="compact"
                                                        half-increments
                                                        readonly
                                                    />
                                                </span>
                                            </div>
                                        </template>
                                        <span>
                                            {{ $t('rating.general') }}: {{ r.rating_general }}/5 · {{ $t('rating.communication') }}: {{ r.rating_communication }}/5 · {{ $t('rating.deliveryTime') }}: {{ r.rating_delivery_time }}/5
                                        </span>
                                    </v-tooltip>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-card-text>
                    </div>
                </v-expand-transition>
            </v-card>
        </v-col>
        <!-- Right column: Info, Documents, Incidents, ANAF, TVA VIES UE -->
        <v-col cols="12" md="6">
            <BulletCard
                class="mt-3"
                :cardTitle="$t('company.cardInfoTitle')"
                titleFontSize="20px"
                :bullets="infoBullets"
            />
            <DocumentManager
                class="mt-3"
                :documents="company.documents"
                :uploadUrl="uploadUrl"
                :deleteUrlPrefix="deleteUrlPrefix"
                :title="$t('company.cardDocsTitle')"
                :onUpload="() => fetchCompany(company.id)"
                :onDelete="() => fetchCompany(company.id)"
                :canEdit="canManageDocuments"
            />
            <BulletCard
                titleColor="#123666"
                toolbarColor="white"
                class="mt-3 bg-primary"
                :cardTitle="$t('company.cardIncidentTitle')"
                :bullets="[]"
                @click="goToCompanyIncidents(company.id)"
            >
                <v-row>
                    <v-col cols="12" md="6">
                        <v-card flat elevation="0" class="bg-primary">
                            <v-card-title class="font-weight-bold">
                                {{ $t("company.paymentIncidentCount") }}:
                                {{ company.payment_incidents.length }}
                            </v-card-title>
                            <v-card-text>
                                <div v-if="company.payment_incidents.length">
                                    <div v-if="paymentDeclined">
                                        {{
                                            $t(
                                                "incidents.statusLabels.declined"
                                            )
                                        }}:
                                        <span
                                            style="
                                                color: #ff5252;
                                                font-weight: bold;
                                            "
                                            >{{ paymentDeclined }}</span
                                        >
                                    </div>
                                    <div v-if="paymentApproved">
                                        {{
                                            $t(
                                                "incidents.statusLabels.approved"
                                            )
                                        }}:
                                        <span
                                            style="
                                                color: #4caf50;
                                                font-weight: bold;
                                            "
                                            >{{ paymentApproved }}</span
                                        >
                                    </div>
                                    <div v-if="paymentPending">
                                        {{
                                            $t(
                                                "incidents.statusLabels.pending"
                                            )
                                        }}:
                                        <span
                                            style="
                                                color: #ffff00;
                                                font-weight: bold;
                                            "
                                            >{{ paymentPending }}</span
                                        >
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col class="bg-primary" cols="12" md="6">
                        <v-card flat elevation="0" class="bg-primary">
                            <v-card-title class="font-weight-bold">
                                {{ $t("company.transportIncidentCount") }}:
                                {{ company.transport_incidents.length }}
                            </v-card-title>
                            <v-card-text>
                                <div v-if="company.transport_incidents.length">
                                    <div v-if="transportDeclined">
                                        {{
                                            $t(
                                                "incidents.statusLabels.declined"
                                            )
                                        }}:
                                        <span
                                            style="
                                                color: #ff5252;
                                                font-weight: bold;
                                            "
                                            >{{ transportDeclined }}</span
                                        >
                                    </div>
                                    <div v-if="transportApproved">
                                        {{
                                            $t(
                                                "incidents.statusLabels.approved"
                                            )
                                        }}:
                                        <span
                                            style="
                                                color: #4caf50;
                                                font-weight: bold;
                                            "
                                            >{{ transportApproved }}</span
                                        >
                                    </div>
                                    <div v-if="transportPending">
                                        {{
                                            $t(
                                                "incidents.statusLabels.pending"
                                            )
                                        }}:
                                        <span
                                            style="
                                                color: #ffff00;
                                                font-weight: bold;
                                            "
                                            >{{ transportPending }}</span
                                        >
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </BulletCard>
            <v-btn
                class="mt-2 w-100"
                color="primary"
                variant="flat"
                :href="'https://www.anaf.ro/RegistruTVA/'"
                target="_blank"
            >
                {{ $t("company.checkAnaf") }}
            </v-btn>
            <v-btn
                class="mt-2 w-100"
                color="primary"
                variant="flat"
                :href="`https://ec.europa.eu/taxation_customs/vies/#/vat-validation?vatNumber=${company.VATNumber}`"
                target="_blank"
            >
                {{ $t("company.checkVatEu") }}
            </v-btn>
            <v-btn
                v-if="isOtherCompany"
                class="mt-2 w-100"
                color="primary"
                variant="flat"
                @click="goToEmployees(company.id)"
            >
                {{ $t("company.viewEmployees") }}
            </v-btn>
        </v-col>
    </v-row>
    </div>
</template>

<script>
import axios from "axios";
import BulletCard from "@/components/display/BulletCard.vue";
import DocumentManager from "@/components/display/DocumentManager.vue";
import FleetEditor from "@/components/forms/FleetEditor.vue";
import dayjs from "dayjs";
import { showAlertModal, formatDisplayDate } from "@/utils";

export default {
    name: "CompanyDetails",
    components: { DocumentManager, BulletCard, FleetEditor },
    data() {
        return {
            user: null,
            company: null,
            newDocuments: [],
            ratings: { average: 0, count: 0, ratings: [] },
            ratingsLoading: false,
            ratingsExpanded: true,
        };
    },
    computed: {
        paymentPending() {
            return (
                this.company?.payment_incidents.filter(
                    (i) => i.status === "pending"
                ).length || 0
            );
        },
        paymentApproved() {
            return (
                this.company?.payment_incidents.filter(
                    (i) => i.status === "approved"
                ).length || 0
            );
        },
        paymentDeclined() {
            return (
                this.company?.payment_incidents.filter(
                    (i) => i.status === "declined"
                ).length || 0
            );
        },
        transportPending() {
            return (
                this.company?.transport_incidents.filter(
                    (i) => i.status === "pending"
                ).length || 0
            );
        },
        transportApproved() {
            return (
                this.company?.transport_incidents.filter(
                    (i) => i.status === "approved"
                ).length || 0
            );
        },
        transportDeclined() {
            return (
                this.company?.transport_incidents.filter(
                    (i) => i.status === "declined"
                ).length || 0
            );
        },
        isOtherCompany() {
            const userData = sessionStorage.getItem("user");
            if (!userData || !this.company) return false;
            const userCompanyId = JSON.parse(userData).company;
            return userCompanyId !== this.company.id;
        },
        uploadUrl() {
            return `${process.env.VUE_APP_API_URL}/companies/${this.company?.id}/documents/`;
        },
        deleteUrlPrefix() {
            return `${process.env.VUE_APP_API_URL}/companies/${this.company?.id}/documents`;
        },
        /**
         * Convert company.type integer to a human-readable label
         * using i18n for the unknown text.
         */
        typeDisplay() {
            if (!this.company) return "";
            switch (this.company.type) {
                case 0:
                    return "Transportator"; // or use your translations if you have them
                case 1:
                    return "Spedition";
                case 2:
                    return "Producator";
                default:
                    return this.$t("company.unknownType");
            }
        },
        /**
         * Card #1: Info
         * "Tip cont" and "Abonament"
         */
        infoBullets() {
            const months = this.company?.subscription_months;
            const abonamentValue = months
                ? `${months} ${this.$t("register.subscriptionMonths")}`
                : "‑";

            const registrationDate = this.company?.created_at
                ? formatDisplayDate(this.company.created_at)
                : this.$t("company.nA");

            const expirationDate =
                this.company?.subscription_expiry
                    ? formatDisplayDate(this.company.subscription_expiry)
                    : this.$t("company.nA");

            return [
                `${this.$t("company.memberId")}: ${this.company.id}`,
                `${this.$t("company.registrationDate")}: ${registrationDate}`,
                `${this.$t("company.accountType")}: ${this.typeDisplay}`,
                `${this.$t("company.abonament")}: ${abonamentValue}`,
                `${this.$t(
                    "company.subscriptionExpiresAt"
                )}: ${expirationDate}`,
            ];
        },
        /**
         * Card #2: Firma
         * "Denumire", "inregistrare comerciala", "nr de TVA", "anul infintarii"
         */
        firmaBullets() {
            const name = this.company.name || this.$t("company.nA");
            const vat = this.company.VATNumber || this.$t("company.nA");
            const regOrder =
                this.company.registry_orderNumber || this.$t("company.nA");
            const regUnique =
                this.company.registry_uniqueId || this.$t("company.nA");
            const regYear = this.company.registry_year || this.$t("company.nA");
            const fax = this.company.fax || this.$t("company.nA");

            const commercialRegistration = `${regOrder}-${regUnique}-${regYear}`;

            const employeesCount =
                this.company.employees_count ?? this.company.users?.length ?? 0;

            return [
                `${this.$t("company.name")}: ${name}`,
                `${this.$t(
                    "company.commercialRegistration"
                )}: ${commercialRegistration}`,
                `${this.$t("company.nrDeTva")}: ${vat}`,
                `${this.$t("company.fax")}: ${fax}`,
                `${this.$t("company.establishmentYear")}: ${regYear}`,
                `${this.$t("company.employees")}: ${employeesCount}`,
            ];
        },
        /**
         * Card #3: Contact
         * "Telefon", "Email", "Adresă"
         */
        contactBullets() {
            const t = this.$t.bind(this);

            /* ---------- phone helper ---------- */
            const formatPhone = ({ prefix = "", number = "" } = {}) => {
                if (!number) return null;
                const plus = prefix
                    ? prefix.startsWith("+")
                        ? prefix
                        : `+${prefix}`
                    : "";
                return plus ? `${plus} ${number}` : number;
            };

            /* ---------- phones ---------- */
            const phones = [];

            const mainPhone = formatPhone({
                prefix: this.company.admin_user?.phone_prefix,
                number: this.company.admin_user?.phone,
            });
            if (mainPhone) phones.push(mainPhone);

            (this.company.admin_user?.phones || []).forEach((p) => {
                const extra = formatPhone(p);
                if (extra) phones.push(extra);
            });

            const phonesLine = phones.length
                ? phones.join(", ")
                : t("company.nA");

            /* ---------- e-mails ---------- */
            const emails = [];

            const mainEmail = this.company.admin_user?.email;
            if (mainEmail) emails.push(mainEmail);

            (this.company.admin_user?.alt_emails || []).forEach((ae) => {
                if (ae.email) emails.push(ae.email);
            });

            const emailsLine = emails.length
                ? emails.join(", ")
                : t("company.nA");

            /* ---------- address ---------- */
            const street = this.company.street || "";
            const number = this.company.number || "";
            const postal = this.company.postal_code || "";
            const city = this.company.city || "";
            const country = this.company.country || "";
            const fullAddress =
                `${street} ${number} ${postal}, ${city}, ${country}`.trim();

            /* ---------- bullets ---------- */
            return [
                `${t("company.phone")}: ${phonesLine}`,
                `${t("company.email")}: ${emailsLine}`,
                `${t("company.address")}: ${fullAddress}`,
            ];
        },
        /** Card #4: Incidents */
        incidentBullets() {
            if (!this.company) return [];

            const paymentIncidents = this.company.payment_incidents || [];
            const transportIncidents = this.company.transport_incidents || [];

            const countByStatus = (arr, status) =>
                arr.filter((i) => i.status === status).length;

            const t = this.$t;

            // ----- Payment counts -----
            const paymentPending = countByStatus(paymentIncidents, "pending");
            const paymentApproved = countByStatus(paymentIncidents, "approved");
            const paymentDeclined = countByStatus(paymentIncidents, "declined");

            let paymentSummary = `${t("company.paymentIncidentCount")}: ${
                paymentIncidents.length
            }`;
            const paymentParts = [];
            if (paymentPending)
                paymentParts.push(
                    `<span style="color: #FFFF00; font-weight: 500;">${t(
                        "incidents.statusLabels.pending"
                    )}: ${paymentPending}</span>`
                );
            if (paymentApproved)
                paymentParts.push(
                    `<span style="color: #4CAF50; font-weight: 500;">${t(
                        "incidents.statusLabels.approved"
                    )}: ${paymentApproved}</span>`
                );
            if (paymentDeclined)
                paymentParts.push(
                    `<span style="color: #FF5252; font-weight: 500;">${t(
                        "incidents.statusLabels.declined"
                    )}: ${paymentDeclined}</span>`
                );
            if (paymentParts.length)
                paymentSummary += ` - ${paymentParts.join(", ")}`;

            // ----- Transport counts -----
            const transportPending = countByStatus(
                transportIncidents,
                "pending"
            );
            const transportApproved = countByStatus(
                transportIncidents,
                "approved"
            );
            const transportDeclined = countByStatus(
                transportIncidents,
                "declined"
            );

            let transportSummary = `${t("company.transportIncidentCount")}: ${
                transportIncidents.length
            }`;
            const transportParts = [];
            if (transportPending)
                transportParts.push(
                    `<span style="color: #FFFF00; font-weight: 500;">${t(
                        "incidents.statusLabels.pending"
                    )}: ${transportPending}</span>`
                );
            if (transportApproved)
                transportParts.push(
                    `<span style="color: #4CAF50; font-weight: 500;">${t(
                        "incidents.statusLabels.approved"
                    )}: ${transportApproved}</span>`
                );
            if (transportDeclined)
                transportParts.push(
                    `<span style="color: #FF5252; font-weight: 500;">${t(
                        "incidents.statusLabels.declined"
                    )}: ${transportDeclined}</span>`
                );
            if (transportParts.length)
                transportSummary += ` - ${transportParts.join(", ")}`;

            return [paymentSummary, transportSummary];
        },

        documentsBullets() {
            if (
                !this.company ||
                !this.company.documents ||
                !this.company.documents.length
            ) {
                return [this.$t("company.nA")]; // or a specialized fallback message
            }

            // For each document, build an HTML link with "Document X"
            return this.company.documents.map((doc, index) => {
                return `<a href="${doc.url}" target="_blank">${this.$t(
                    "company.document"
                )} ${index + 1}</a>`;
            });
        },
        canManageDocuments() {
            const userData = sessionStorage.getItem("user");
            if (!userData || !this.company) return false;

            const user = JSON.parse(userData);
            return user.company === this.company.id;
        },
    },
    created() {
        const companyId = this.$route.params.id;

        if (companyId) {
            this.fetchCompany(companyId);
        } else {
            const userData = sessionStorage.getItem("user");
            if (userData) {
                this.user = JSON.parse(userData);
                if (this.user.company) {
                    this.fetchCompany(this.user.company);
                }
            } else {
                console.warn("No user found in sessionStorage.");
            }
        }
    },
    methods: {
        showAlertModal,
        formatRatingDate(dateStr) {
            return formatDisplayDate(dateStr);
        },
        ratingAverage(r) {
            if (r.rating_average != null) return Number(r.rating_average);
            return (r.rating_general + r.rating_communication + r.rating_delivery_time) / 3;
        },
        goToEmployees(companyId) {
            this.$router.push({
                name: "EmployeesView",
                params: { companyId },
            });
        },
        goToCompanyIncidents(companyId) {
            this.$router.push({
                name: "OtherCompanyIncidents",
                query: { companyId }, // sends companyId as a query param
            });
        },
        async fetchCompany(companyId) {
            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_API_URL}/companies/${companyId}/`
                );
                this.company = response.data;
                this.fetchRatings(companyId);
            } catch (error) {
                console.error("Error fetching company:", error);
                this.showAlertModal(
                    this.$store,
                    "Error fetching company",
                    "danger",
                    12000,
                    error
                );
            }
        },
        async fetchRatings(companyId) {
            this.ratingsLoading = true;
            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_API_URL}/companies/${companyId}/ratings/`
                );
                this.ratings = response.data;
            } catch {
                this.ratings = { average: 0, count: 0, ratings: [] };
            } finally {
                this.ratingsLoading = false;
            }
        },
        openFileDialog() {
            // Trigger the hidden file input
            this.$refs.fileInput.click();
        },
        async uploadDocuments() {
            if (!this.newDocuments || !this.newDocuments.length) return;

            const formData = new FormData();
            // Append each selected file under the key "documents"
            this.newDocuments.forEach((file) => {
                formData.append("documents", file);
            });

            try {
                // POST to the back-end endpoint with the company id in the URL
                const response = await axios.post(
                    `${process.env.VUE_APP_API_URL}/companies/${this.company.id}/documents/`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                // Refresh company data after successful upload
                await this.fetchCompany(this.user.company);
            } catch (error) {
                // If the server returns a 400 error, display the error message to the user
                if (error.response && error.response.status === 400) {
                    this.showAlertModal(
                        this.$store,
                        "File upload failed.",
                        "danger",
                        12000,
                        error
                    );
                } else {
                    this.showAlertModal(
                        this.$store,
                        "An unexpected error occurred during file upload.",
                        "danger",
                        12000,
                        error
                    );
                }
                console.error("Error uploading documents:", error);
            } finally {
                // Clear the newDocuments array regardless of success/failure
                this.newDocuments = [];
            }
        },
        async deleteDocument(docId) {
            if (!confirm(this.$t("company.confirmDeleteDocument"))) return;

            try {
                await axios.delete(
                    `${process.env.VUE_APP_API_URL}/companies/${this.company.id}/documents/${docId}/`
                );
                await this.fetchCompany(this.company.id);
            } catch (error) {
                console.error("Error deleting document:", error);
                this.showAlertModal(
                    this.$store,
                    this.$t("company.deleteFailed"),
                    "danger",
                    12000,
                    error
                );
            }
        },
    },
};
</script>

<style scoped>
/* Align list content with card title (same as Fleet card) */
.company-details-view :deep(.v-list-item) {
    padding-inline-start: 0 !important;
}

.rating-row:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.rating-row-content {
    align-items: center;
}

.rating-row-content .v-rating {
    flex-shrink: 0;
}
</style>
